import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { userTenantFeature } from '@core/user-tenant/user-tenant.reducer';
import { resolveBootstrapTenantId } from '@core/user-tenant/tenant-context';

/**
 * localStorage is shared across tabs and, without namespacing, across
 * tenants selected via the tenant switcher. This service prefixes keys
 * with the currently selected tenant id so tenants don't clobber each
 * other's locally-persisted data (autosaved forms, UI settings, etc).
 *
 * Reads transparently migrate pre-rollout, un-prefixed ("legacy") keys
 * to the tenant-prefixed key the first time they're read for a resolved
 * tenant, so data saved before this rollout (or by tests/e2e that seed
 * bare keys) is not lost.
 */
@Injectable({
  providedIn: 'root',
})
export class TenantLocalStorageService {
  private readonly store = inject(Store);
  private readonly selectedTenantId = this.store.selectSignal(userTenantFeature.selectSelectedTenantId);

  /**
   * Resolves synchronously on every call (not cached at construction) so
   * that tenant switches -- which happen without recreating this
   * providedIn: 'root' singleton -- are reflected immediately.
   */
  private resolveTenantId(): string | null {
    return this.selectedTenantId() ?? resolveBootstrapTenantId();
  }

  private prefixedKey(tenantId: string | null, key: string): string {
    return tenantId ? `tenant:${tenantId}:${key}` : key;
  }

  /**
   * The key actually used to store/retrieve `key` for the current tenant.
   * Returns `key` unchanged when no tenant can be resolved (pre-login edge).
   */
  keyFor(key: string): string {
    return this.prefixedKey(this.resolveTenantId(), key);
  }

  getItem(key: string): string | null {
    const tenantId = this.resolveTenantId();
    const prefixedKey = this.prefixedKey(tenantId, key);
    const value = localStorage.getItem(prefixedKey);
    if (value != null) {
      return value;
    }

    if (tenantId) {
      const legacyValue = localStorage.getItem(key);
      if (legacyValue != null) {
        localStorage.setItem(prefixedKey, legacyValue);
        localStorage.removeItem(key);
        return legacyValue;
      }
    }

    return null;
  }

  setItem(key: string, value: string): void {
    localStorage.setItem(this.keyFor(key), value);
  }

  removeItem(key: string): void {
    localStorage.removeItem(this.keyFor(key));
    // Also clear any un-migrated legacy bare key so it can't resurface.
    localStorage.removeItem(key);
  }

  getJson<T>(key: string): T | null {
    const raw = this.getItem(key);
    return raw != null ? JSON.parse(raw) as T : null;
  }

  setJson(key: string, value: unknown): void {
    this.setItem(key, JSON.stringify(value));
  }
}
